import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import {
  PageContainer,
  Header,
  HeaderTop,
  CourseIcon,
  HeaderContent,
  Title,
  Subtitle,
  StatusBadge,
  InfoGrid,
  InfoCard,
  InfoLabel,
  InfoValue,
  DocumentLink,
  AttendeeSection,
  SectionHeader,
  SectionTitle,
  AttendeeGrid,
  AttendeeCard,
  AttendeeAvatar,
  AttendeeInfo,
  AttendeeName,
  AttendeeId,
  SignedBadge,
  ActionsBar,
  PrimaryButton,
  SecondaryButton,
  CreateButton,
  AttendeeGridNotSigned,
} from "./attendanceListNew.styled";
import InfiniteList from "../../../utils/infiniteList";
import PdfViewer from "../../../utils/PdfViewer";

const AttendanceListNewView = ({
  session,
  listAttendees,
  forceRefresh,
  onSignAttendance,
  onDeleteAttendance,
  onGenerateDocument,
  onBack,
  onCreateViewer,
}) => {
  const { t } = useTranslation();
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [currentPdfTitle, setCurrentPdfTitle] = useState("");

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  const renderAttendeeCard = (attendee, session) => {
    const hasSigned = attendee.signature && attendee.signature !== "";

    return (
      <AttendeeCard
        key={attendee.uuid}
        $signed={hasSigned}
        onClick={
          !session?.completed && !hasSigned
            ? () => onSignAttendance(attendee.uuid)
            : undefined
        }
      >
        {hasSigned && (
          <SignedBadge>
            <i className="fas fa-check"></i>
            {t("attendanceListNew.status.signed")}
          </SignedBadge>
        )}

        <AttendeeAvatar $signed={hasSigned}>
          {hasSigned ? (
            <i className="fas fa-signature"></i>
          ) : (
            getInitials(attendee.name)
          )}
        </AttendeeAvatar>

        <AttendeeInfo>
          <AttendeeName>{attendee.name}</AttendeeName>
          <AttendeeId>{attendee.nif}</AttendeeId>
        </AttendeeInfo>
      </AttendeeCard>
    );
  };

  const getFilteredAttendees = async (...args) => {
    const result = await listAttendees(...args);

    if (session.completed && result?.docs) {
      const signedAttendees = result.docs.filter(
        (attendee) => attendee.signature && attendee.signature !== ""
      );

      return {
        ...result,
        docs: signedAttendees,
        totalDocs: signedAttendees.length,
      };
    }

    return result;
  };

  const renderNotSignedCard = (attendee, session) => {
    return (
      <AttendeeCard
        key={attendee.uuid}
        $signed={false}
        style={{ opacity: 0.6, backgroundColor: "#f5f5f5" }}
      >
        <AttendeeAvatar $signed={false}>
          {getInitials(attendee.name)}
        </AttendeeAvatar>

        <AttendeeInfo>
          <AttendeeName style={{ color: "#666" }}>{attendee.name}</AttendeeName>
          <AttendeeId style={{ color: "#999" }}>{attendee.nif}</AttendeeId>
        </AttendeeInfo>
      </AttendeeCard>
    );
  };

  const handlePreviewPdf = (url, title) => {
    setCurrentPdfUrl(url);
    setCurrentPdfTitle(title);
    setShowPdfViewer(true);
  };

  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    setCurrentPdfUrl(null);
    setCurrentPdfTitle("");
  };

  return (
    <PageContainer>
      {/* Header con información de la sesión */}
      <Header>
        <HeaderTop>
          <CourseIcon $completed={session.completed}>
            <i className="fas fa-graduation-cap"></i>
          </CourseIcon>
          <HeaderContent>
            <Title>{session.meeting?.course?.name || "N/A"}</Title>
            <Subtitle>{t("attendanceListNew.subtitle")}</Subtitle>
          </HeaderContent>
          {!session?.completed && (
            <CreateButton onClick={() => onCreateViewer(session.uuid)}>
              <i className="fas fa-user-plus"></i>
              {t("attendanceListNew.buttons.addViewer")}
            </CreateButton>
          )}
          <StatusBadge $completed={session.completed}>
            {session.completed
              ? t("attendanceListNew.status.completed")
              : t("attendanceListNew.status.pending")}
          </StatusBadge>
        </HeaderTop>

        <InfoGrid>
          <InfoCard>
            <InfoLabel>
              <i className="fas fa-calendar"></i>
              {t("attendanceListNew.labels.sessionDate")}
            </InfoLabel>
            <InfoValue>
              {moment(session.date).format("dddd, DD MMMM YYYY")}
            </InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>
              <i className="fas fa-clock"></i>
              {t("attendanceListNew.labels.duration")}
            </InfoLabel>
            <InfoValue>{moment(session.date).format("HH:mm")}</InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>
              <i className="fas fa-user"></i>
              {t("attendanceListNew.labels.trainer")}
            </InfoLabel>
            <InfoValue>{session.user?.name || "N/A"}</InfoValue>
          </InfoCard>

          <InfoCard>
            <InfoLabel>
              <i className="fas fa-map-marker-alt"></i>
              {t("attendanceListNew.labels.location")}
            </InfoLabel>
            <InfoValue>{session.meeting?.center?.name || "N/A"}</InfoValue>
          </InfoCard>
        </InfoGrid>

        {session.meeting?.course?.documents &&
          session.meeting.course.documents.length > 0 && (
            <>
              {/* <div className="row"> */}
              <div style={{ marginTop: "16px" }}>
                <InfoLabel
                  style={{
                    color: "rgba(255, 255, 255, 0.7)",
                    marginBottom: "12px",
                  }}
                >
                  <i className="fas fa-file-alt"></i>
                  {t("attendanceListNew.labels.courseMaterials")}
                </InfoLabel>
                <DocumentLink
                  href={session.meeting.course.documents[0]?.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-download"></i>
                  {session.meeting.course.documents[0]?.name ||
                    t("attendanceListNew.buttons.download")}
                </DocumentLink>
                <DocumentLink
                  as="button"
                  onClick={() =>
                    handlePreviewPdf(
                      session.meeting.course.documents[0]?.url,
                      session.meeting.course.documents[0]?.name
                    )
                  }
                  style={{
                    background: "none",
                    border: "none",
                    textAlign: "left",
                    padding: 0,
                  }}
                >
                  <i className="fas fa-eye"></i>
                  {t("attendanceListNew.buttons.preview")}
                </DocumentLink>
              </div>
              {/* </div> */}
            </>
          )}
      </Header>

      {/* Sección de asistentes */}
      <AttendeeSection>
        <SectionHeader>
          <SectionTitle>
            <i className="fas fa-users"></i>
            {t("attendanceListNew.attendeesTitle")}
          </SectionTitle>
        </SectionHeader>

        {/* {!session.completed ? ( */}
        <AttendeeGrid>
          <InfiniteList
            id="attendees-list"
            toList={getFilteredAttendees}
            renderItem={(attendee) => renderAttendeeCard(attendee, session)}
            defaultPageSize={20}
            forceRefresh={forceRefresh}
            tbLoadingText="attendanceListNew.loading"
            tbNoDataText="attendanceListNew.noAttendees"
            customClassName="attendees-infinite-scroll"
            iconEmptyList="fas fa-user-slash"
          />
        </AttendeeGrid>

        {session.completed &&
          session.employeesNotSigned &&
          session.employeesNotSigned.length > 0 && (
            <>
              <SectionHeader style={{ marginTop: "2rem" }}>
                <SectionTitle>
                  <i className="fas fa-user-times"></i>
                  {t("attendanceListNew.notSignedTitle") ||
                    "Empleados no firmados"}
                </SectionTitle>
              </SectionHeader>
              <AttendeeGridNotSigned>
                {session.employeesNotSigned.map((employee) =>
                  renderNotSignedCard(employee, session)
                )}
              </AttendeeGridNotSigned>
            </>
          )}

        <PdfViewer
          show={showPdfViewer}
          onHide={handleClosePdfViewer}
          pdfUrl={currentPdfUrl}
          title={currentPdfTitle}
        />

        {/* ) : (
          <AttendeeGrid>
            <p>No hay asistentes registrados.</p>
          </AttendeeGrid>
        )} */}
      </AttendeeSection>

      {/* Barra de acciones */}
      <ActionsBar>
        <PrimaryButton onClick={onGenerateDocument}>
          <i className="fas fa-file-download"></i>
          {t("attendanceListNew.buttons.generateDocument")}
        </PrimaryButton>
        <SecondaryButton onClick={onBack}>
          <i className="fas fa-arrow-left"></i>
          {t("attendanceListNew.buttons.back")}
        </SecondaryButton>
      </ActionsBar>
    </PageContainer>
  );
};

AttendanceListNewView.propTypes = {
  session: PropTypes.object.isRequired,
  listAttendees: PropTypes.func.isRequired,
  forceRefresh: PropTypes.bool.isRequired,
  onSignAttendance: PropTypes.func.isRequired,
  onDeleteAttendance: PropTypes.func.isRequired,
  onGenerateDocument: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onCreateViewer: PropTypes.func.isRequired,
};

export default AttendanceListNewView;
